import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { MEMORY_EDITABLE_FIELDS } from '../property/memory.schemas';

/**
 * A logged e-mail, call or note on one of an Objekt's deals is to be read into that
 * Objekt's memory (IMM-1050.5). The property module publishes it to itself so the
 * model call runs on the property lane, not inside the `activity.*` delivery that
 * would hold the timeline projection behind it. `text` is what the model reads.
 * `audited: never` — a work trigger carrying record content, not a domain fact.
 */
export const ActivityMemoryReadRequestedPayloadSchema = z.object({
  activityId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid().nullable(),
  propertyId: z.uuid(),
  sourceType: z.enum(['EMAIL', 'CALL', 'NOTE']),
  sourceName: z.string().nullable(),
  text: z.string(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type ActivityMemoryReadRequestedPayload = z.infer<typeof ActivityMemoryReadRequestedPayloadSchema>;

/**
 * The channel an operator's memory change came through (IMM-1157): an `adm_` credential on the
 * admin MCP. Nested, never a top-level `credentialClass`, because `audit_event_search` drops rows
 * whose top-level `credentialClass` is `backoffice-credential` as the surface's own traffic.
 */
export const MemoryChangeViaSchema = z.object({
  credentialClass: z.literal('backoffice-credential'),
  credentialId: z.uuid(),
});

/**
 * An operator changed one Objekt-memory entry (IMM-1157). Audit only: ids and the action, never the
 * statement or its value — the row is the record of what changed, and the audit log is no second
 * copy of what a document said.
 */
export const MemoryEntryChangedPayloadSchema = z.object({
  entryId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  action: z.enum(['corrected', 'deleted', 'edited', 'added']),
  actor: ActorSchema,
  via: MemoryChangeViaSchema,
  occurredAt: z.iso.datetime(),
  // Present exactly on `edited`: the fields that call changed, never their values.
  editedFields: z.array(z.enum(MEMORY_EDITABLE_FIELDS)).optional(),
});

export type MemoryChangeVia = z.infer<typeof MemoryChangeViaSchema>;
export type MemoryEntryChangedPayload = z.infer<typeof MemoryEntryChangedPayloadSchema>;
